(() => {
  if (window.orrProductListHistory) return;

  const VERSION = 1;
  const MARKUP_VERSION = 3;
  const STATE_KEY = 'orrProductListEntryId';
  const STORAGE_PREFIX = 'orr:product-list:v1:';
  const DATA_PREFIX = `${STORAGE_PREFIX}data:`;
  const POSITION_PREFIX = `${STORAGE_PREFIX}position:`;
  const INDEX_KEY = `${STORAGE_PREFIX}index`;
  const PENDING_KEY = `${STORAGE_PREFIX}pending`;
  const TTL = 30 * 60 * 1000;
  const MAX_ENTRIES = 3;
  const controllers = new Set();
  const grids = new WeakMap();
  const pendingControllers = new Set();
  let scrollTimer;
  let discoverTimer;
  let skipNextPopstate = false;
  let popstateRestored = false;

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  function createId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function canonicalUrl(value = window.location.href) {
    const url = new URL(value, window.location.origin);
    return `${url.pathname}${url.search}`;
  }

  function readJson(key) {
    try {
      const value = sessionStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (_) {
      return null;
    }
  }

  function removeEntry(entryId) {
    try {
      sessionStorage.removeItem(`${DATA_PREFIX}${entryId}`);
      sessionStorage.removeItem(`${POSITION_PREFIX}${entryId}`);
    } catch (_) {}
  }

  function readIndex() {
    const index = readJson(INDEX_KEY);
    return Array.isArray(index) ? index.filter((entry) => entry && typeof entry.id === 'string') : [];
  }

  function writeIndex(entryId) {
    const now = Date.now();
    const nextIndex = readIndex()
      .filter((entry) => entry.id !== entryId && now - Number(entry.savedAt || 0) <= TTL)
      .slice(-(MAX_ENTRIES - 1));

    nextIndex.push({ id: entryId, savedAt: now });

    const keep = new Set(nextIndex.map((entry) => entry.id));
    for (const entry of readIndex()) {
      if (!keep.has(entry.id)) removeEntry(entry.id);
    }

    try {
      sessionStorage.setItem(INDEX_KEY, JSON.stringify(nextIndex));
    } catch (_) {}
  }

  function cleanupExpiredEntries() {
    const now = Date.now();
    const validEntries = [];

    for (const entry of readIndex()) {
      if (now - Number(entry.savedAt || 0) <= TTL) {
        validEntries.push(entry);
      } else {
        removeEntry(entry.id);
      }
    }

    try {
      sessionStorage.setItem(INDEX_KEY, JSON.stringify(validEntries.slice(-MAX_ENTRIES)));
    } catch (_) {}
  }

  function writeWithRetry(key, value, entryId) {
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch (_) {
      const candidates = readIndex().filter((entry) => entry.id !== entryId);

      for (const candidate of candidates) {
        removeEntry(candidate.id);
        try {
          sessionStorage.setItem(key, value);
          return true;
        } catch (_) {}
      }

      try {
        sessionStorage.removeItem(key);
        sessionStorage.setItem(key, value);
        return true;
      } catch (_) {
        try {
          sessionStorage.removeItem(key);
        } catch (_) {}
        return false;
      }
    }
  }

  function getHistoryState() {
    return history.state && typeof history.state === 'object' ? history.state : {};
  }

  function ensureHistoryEntryId(preferredId) {
    const state = getHistoryState();
    const currentId = typeof state[STATE_KEY] === 'string' ? state[STATE_KEY] : '';
    const entryId = currentId || preferredId || createId();

    if (currentId !== entryId) {
      history.replaceState({ ...state, [STATE_KEY]: entryId }, '', window.location.href);
    }

    return entryId;
  }

  function mergeHistoryState(nextState) {
    const currentState = getHistoryState();
    const providedState = nextState && typeof nextState === 'object' ? nextState : {};
    const entryId =
      currentState[STATE_KEY] || providedState[STATE_KEY] || ensureHistoryEntryId();

    return {
      ...currentState,
      ...providedState,
      [STATE_KEY]: entryId,
    };
  }

  function getItemIdentifier(item) {
    const link = item.matches('a[href]') ? item : item.querySelector('a[href]');
    let href = '';

    if (link instanceof HTMLAnchorElement) {
      const url = new URL(link.href, window.location.origin);
      href = `${url.pathname}${url.search}`;
    }

    return {
      id: item.id || '',
      productId: item.getAttribute('data-product-id') || '',
      href,
    };
  }

  function getAnchor(grid, itemSelector) {
    const items = [...grid.querySelectorAll(itemSelector)];
    const item = items.find((candidate) => candidate.getBoundingClientRect().bottom > 0);
    if (!(item instanceof HTMLElement)) return null;

    return {
      ...getItemIdentifier(item),
      offset: item.getBoundingClientRect().top,
    };
  }

  function findAnchor(grid, itemSelector, anchor) {
    if (!anchor) return null;

    for (const item of grid.querySelectorAll(itemSelector)) {
      if (!(item instanceof HTMLElement)) continue;
      const identifier = getItemIdentifier(item);
      if (anchor.id && identifier.id === anchor.id) return item;
      if (anchor.productId && identifier.productId === anchor.productId) return item;
      if (anchor.href && identifier.href === anchor.href) return item;
    }

    return null;
  }

  function getGridAttributes(grid, names) {
    const attributes = {};

    for (const name of names) {
      attributes[name] = grid.hasAttribute(name) ? grid.getAttribute(name) : null;
    }

    return attributes;
  }

  function applyGridAttributes(grid, attributes) {
    if (!attributes || typeof attributes !== 'object') return;

    for (const [name, value] of Object.entries(attributes)) {
      if (value === null) {
        grid.removeAttribute(name);
      } else {
        grid.setAttribute(name, String(value));
      }
    }
  }

  function getPaginationState(grid, itemSelector) {
    const loadedPages = [
      ...new Set(
        [...grid.querySelectorAll(itemSelector)]
          .map((item) => Number(item.getAttribute('data-page')))
          .filter((page) => Number.isInteger(page) && page > 0)
      ),
    ].sort((a, b) => a - b);

    if (loadedPages.length === 0) {
      const currentPage = Number(new URL(window.location.href).searchParams.get('page') || 1);
      if (Number.isInteger(currentPage) && currentPage > 0) loadedPages.push(currentPage);
    }

    return {
      nextUrl: grid.getAttribute('data-next-url') || '',
      loadedPages,
      firstLoadedPage: loadedPages[0] || 1,
      lastLoadedPage: loadedPages[loadedPages.length - 1] || 1,
      itemCount: grid.querySelectorAll(itemSelector).length,
    };
  }

  function serializeGrid(grid) {
    const clone = grid.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return '';

    for (const redundantElement of clone.querySelectorAll('script, noscript')) {
      redundantElement.remove();
    }

    const seenStyles = new Set();
    for (const style of clone.querySelectorAll('style')) {
      const signature = style.textContent?.trim() || '';
      if (seenStyles.has(signature)) {
        style.remove();
      } else {
        seenStyles.add(signature);
      }
    }

    return clone.innerHTML;
  }

  function getLoadMoreState(element) {
    if (!(element instanceof HTMLElement)) return null;

    return {
      html: element.innerHTML,
      display: element.style.display,
      hidden: element.hidden,
    };
  }

  function applyLoadMoreState(element, state) {
    if (!(element instanceof HTMLElement) || !state) return;
    element.innerHTML = typeof state.html === 'string' ? state.html : '';
    element.style.display = typeof state.display === 'string' ? state.display : '';
    element.hidden = Boolean(state.hidden);
  }

  class ProductListHistoryController {
    constructor(config) {
      this.root = config.root;
      this.grid = config.grid;
      this.listKey = config.listKey;
      this.itemSelector = config.itemSelector;
      this.attributes = config.attributes;
      this.loadMore = config.loadMore;
      this.entryId = ensureHistoryEntryId();
      this.renderedUrl = canonicalUrl();
      this.restoring = false;
      this.restoredEntryId = '';
      this.captureTimer = null;
      this.initialRestorePending = this.shouldRestoreInitialState();
      this.ready = this.restoreInitialState();

      this.observer = new MutationObserver(() => {
        clearTimeout(this.captureTimer);
        this.captureTimer = setTimeout(() => {
          this.syncHistoryEntry();
          this.captureContent();
        }, 100);
      });

      this.observer.observe(this.grid, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: this.attributes,
      });

      if (this.loadMore) {
        this.observer.observe(this.loadMore, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden'],
        });
      }

      if (!this.restoring && !this.initialRestorePending) {
        this.captureContent();
        this.capturePosition();
      }
    }

    syncHistoryEntry() {
      const state = getHistoryState();
      const stateEntryId = typeof state[STATE_KEY] === 'string' ? state[STATE_KEY] : '';

      if (!stateEntryId) {
        this.entryId = ensureHistoryEntryId(createId());
        this.renderedUrl = canonicalUrl();
        return;
      }

      if (stateEntryId !== this.entryId) {
        this.entryId = stateEntryId;
        this.renderedUrl = canonicalUrl();
      }
    }

    captureContent() {
      if (this.restoring || this.initialRestorePending || !this.grid.isConnected) return;
      this.entryId = ensureHistoryEntryId(this.entryId);
      this.renderedUrl = canonicalUrl();

      const payload = {
        version: VERSION,
        markupVersion: MARKUP_VERSION,
        entryId: this.entryId,
        listKey: this.listKey,
        url: this.renderedUrl,
        html: serializeGrid(this.grid),
        attributes: getGridAttributes(this.grid, this.attributes),
        pagination: getPaginationState(this.grid, this.itemSelector),
        loadMore: getLoadMoreState(this.loadMore),
        savedAt: Date.now(),
      };

      const saved = writeWithRetry(`${DATA_PREFIX}${this.entryId}`, JSON.stringify(payload), this.entryId);
      if (saved) {
        writeIndex(this.entryId);
      }
    }

    capturePosition() {
      if (this.restoring || this.initialRestorePending || !this.grid.isConnected) return;

      const payload = {
        version: VERSION,
        entryId: this.entryId,
        listKey: this.listKey,
        url: canonicalUrl(),
        scrollY: window.scrollY,
        anchor: getAnchor(this.grid, this.itemSelector),
        savedAt: Date.now(),
      };

      writeWithRetry(`${POSITION_PREFIX}${this.entryId}`, JSON.stringify(payload), this.entryId);
    }

    readState(entryId = this.entryId) {
      const data = readJson(`${DATA_PREFIX}${entryId}`);
      const position = readJson(`${POSITION_PREFIX}${entryId}`);
      const now = Date.now();

      if (
        !data ||
        data.version !== VERSION ||
        data.markupVersion !== MARKUP_VERSION ||
        data.entryId !== entryId ||
        typeof data.html !== 'string' ||
        now - Number(data.savedAt || 0) > TTL
      ) {
        removeEntry(entryId);
        return null;
      }

      if (data.listKey !== this.listKey || data.url !== canonicalUrl()) return null;

      if (
        position &&
        (position.version !== VERSION ||
          position.entryId !== entryId ||
          position.listKey !== this.listKey ||
          now - Number(position.savedAt || 0) > TTL)
      ) {
        return { data, position: null };
      }

      return { data, position };
    }

    shouldRestoreInitialState() {
      const pending = readJson(PENDING_KEY);
      if (!pending || pending.version !== VERSION) return false;

      return (
        pending.entryId === this.entryId &&
        pending.listKey === this.listKey &&
        pending.url === canonicalUrl() &&
        Date.now() - Number(pending.savedAt || 0) <= TTL
      );
    }

    async completeInitialRestore() {
      const restored = await this.restoreEntry(this.entryId, true);
      this.initialRestorePending = false;
      this.captureContent();
      this.capturePosition();
      return restored;
    }

    restoreInitialState() {
      if (!this.initialRestorePending) return Promise.resolve(false);
      if (document.readyState !== 'loading') {
        return this.completeInitialRestore();
      }

      return new Promise((resolve) => {
        document.addEventListener(
          'DOMContentLoaded',
          () => {
            this.completeInitialRestore().then(resolve);
          },
          { once: true }
        );
      });
    }

    correctRestoredPosition() {
      if (!this.restoredEntryId || this.renderedUrl !== canonicalUrl()) return false;
      const state = this.readState(this.restoredEntryId);
      if (!state?.position) return false;

      requestAnimationFrame(() => {
        this.restorePosition(state.position);
      });
      return true;
    }

    restoreEntry(entryId, clearPending = false) {
      if (this.restoredEntryId === entryId && this.renderedUrl === canonicalUrl()) {
        if (clearPending) {
          try {
            sessionStorage.removeItem(PENDING_KEY);
          } catch (_) {}
        }
        return Promise.resolve(true);
      }

      const state = this.readState(entryId);
      if (!state) return Promise.resolve(false);

      this.restoring = true;
      this.entryId = entryId;
      this.grid.innerHTML = state.data.html;
      applyGridAttributes(this.grid, state.data.attributes);
      applyLoadMoreState(this.loadMore, state.data.loadMore);
      this.renderedUrl = canonicalUrl();
      this.restoredEntryId = entryId;

      if (clearPending) {
        try {
          sessionStorage.removeItem(PENDING_KEY);
        } catch (_) {}
      }

      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.restorePosition(state.position);
            setTimeout(() => {
              this.restorePosition(state.position);
              this.restoring = false;
              this.captureContent();
              this.capturePosition();
              this.root.dispatchEvent(
                new CustomEvent('orr:product-list:restored', {
                  bubbles: true,
                  detail: { entryId: this.entryId, listKey: this.listKey },
                })
              );
              resolve(true);
            }, 250);
          });
        });
      });
    }

    restoreCachedPosition(entryId) {
      const state = this.readState(entryId);
      if (!state?.position) return Promise.resolve(false);

      this.restoring = true;
      this.entryId = entryId;
      this.renderedUrl = canonicalUrl();
      this.restoredEntryId = entryId;

      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.restorePosition(state.position);
            setTimeout(() => {
              this.restorePosition(state.position);
              this.restoring = false;
              this.capturePosition();
              this.root.dispatchEvent(
                new CustomEvent('orr:product-list:restored', {
                  bubbles: true,
                  detail: { entryId: this.entryId, listKey: this.listKey },
                })
              );
              resolve(true);
            }, 250);
          });
        });
      });
    }

    restorePosition(position) {
      if (!position) return;
      const anchor = findAnchor(this.grid, this.itemSelector, position.anchor);

      if (anchor) {
        const offset = Number(position.anchor?.offset || 0);
        const top = window.scrollY + anchor.getBoundingClientRect().top - offset;
        window.scrollTo({ top, behavior: 'instant' });
        return;
      }

      window.scrollTo({ top: Number(position.scrollY || 0), behavior: 'instant' });
    }

    markPendingReturn() {
      this.entryId = ensureHistoryEntryId(this.entryId);
      this.captureContent();
      this.capturePosition();

      const pending = {
        version: VERSION,
        entryId: this.entryId,
        listKey: this.listKey,
        url: canonicalUrl(),
        savedAt: Date.now(),
      };

      writeWithRetry(PENDING_KEY, JSON.stringify(pending), this.entryId);
    }

    containsTarget(target) {
      return target instanceof Node && this.root.contains(target);
    }

    isProductNavigation(target, event) {
      if (!(target instanceof Element)) return false;
      if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) {
        return false;
      }

      const item = target.closest(this.itemSelector);
      if (!(item instanceof HTMLElement) || !this.grid.contains(item)) return false;

      const directLink = target.closest('a[href]');
      if (directLink instanceof HTMLAnchorElement) {
        return new URL(directLink.href, window.location.origin).pathname.includes('/products/');
      }

      if (target.closest('button, input, label, select, textarea, [role="button"]')) return false;
      const productLink = item.matches('a[href]') ? item : item.querySelector('a[href*="/products/"]');
      return productLink instanceof HTMLAnchorElement;
    }

    destroy() {
      clearTimeout(this.captureTimer);
      this.observer.disconnect();
      controllers.delete(this);
      grids.delete(this.grid);
    }
  }

  function getListConfig(grid) {
    const section = grid.closest('.shopify-section');
    const root =
      grid.closest('results-list, search-results-loader') ||
      section ||
      grid.parentElement;

    if (!(root instanceof HTMLElement)) return null;

    let itemSelector = '.product-card-wrapper';
    if (grid.matches('[ref="grid"]')) itemSelector = '[ref="cards[]"]';
    if (grid.classList.contains('orr-pl-grid')) itemSelector = '.orr-item';

    const sectionKey =
      root.getAttribute('section-id') ||
      section?.id ||
      root.getAttribute('data-section-id') ||
      root.tagName.toLowerCase();

    return {
      root,
      grid,
      listKey: `${sectionKey}:${grid.id || grid.getAttribute('ref') || grid.className}`,
      itemSelector,
      attributes: ['data-next-url', 'data-last-page', 'product-grid-view'],
      loadMore: section?.querySelector('.orr-load-more-container') || root.querySelector('.orr-load-more-container'),
    };
  }

  function registerGrid(grid) {
    if (!(grid instanceof HTMLElement) || grids.has(grid)) return;
    const config = getListConfig(grid);
    if (!config) return;

    const controller = new ProductListHistoryController(config);
    grids.set(grid, controller);
    controllers.add(controller);
  }

  function discoverProductLists() {
    for (const controller of [...controllers]) {
      if (!controller.grid.isConnected) controller.destroy();
    }

    document.querySelectorAll('results-list [ref="grid"], search-results-loader #product-grid, .orr-product-grid#product-grid, .orr-pl-grid#product-grid').forEach(registerGrid);
  }

  function scheduleDiscovery() {
    clearTimeout(discoverTimer);
    discoverTimer = setTimeout(discoverProductLists, 50);
  }

  function handleDocumentClick(event) {
    const target = event.target;
    const matchedControllers = [...controllers].filter(
      (controller) => controller.containsTarget(target) && controller.isProductNavigation(target, event)
    );

    if (matchedControllers.length === 0) return;

    queueMicrotask(() => {
      if (event.defaultPrevented) return;
      for (const controller of matchedControllers) {
        pendingControllers.add(controller);
        controller.markPendingReturn();
      }
    });
  }

  function handleScroll() {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      for (const controller of controllers) controller.capturePosition();
    }, 150);
  }

  function handlePageHide() {
    for (const controller of controllers) {
      if (pendingControllers.has(controller)) {
        controller.markPendingReturn();
      } else {
        controller.captureContent();
        controller.capturePosition();
      }
    }
    pendingControllers.clear();
  }

  function handlePageShow(event) {
    if (event.persisted) {
      skipNextPopstate = true;
      const currentId = getHistoryState()[STATE_KEY];
      const pending = readJson(PENDING_KEY);
      const entryId = currentId || pending?.entryId;

      if (typeof entryId === 'string') {
        for (const controller of controllers) {
          controller.restoreCachedPosition(entryId);
        }
      }

      if (pending?.entryId === entryId) {
        try {
          sessionStorage.removeItem(PENDING_KEY);
        } catch (_) {}
      }

      setTimeout(() => {
        skipNextPopstate = false;
      }, 0);
      return;
    }

    for (const controller of controllers) {
      controller.correctRestoredPosition();
    }
  }

  function handlePopstate(event) {
    popstateRestored = false;
    if (skipNextPopstate) {
      popstateRestored = true;
      return;
    }

    const entryId = event.state && typeof event.state[STATE_KEY] === 'string' ? event.state[STATE_KEY] : '';
    if (!entryId) return;

    for (const controller of controllers) {
      const state = controller.readState(entryId);
      if (!state) continue;
      controller.restoreEntry(entryId);
      popstateRestored = true;
    }

    setTimeout(() => {
      popstateRestored = false;
    }, 0);
  }

  cleanupExpiredEntries();
  document.addEventListener('click', handleDocumentClick);
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('popstate', handlePopstate);
  document.addEventListener('DOMContentLoaded', discoverProductLists, { once: true });

  const documentObserver = new MutationObserver(scheduleDiscovery);
  documentObserver.observe(document.documentElement, { childList: true, subtree: true });

  window.orrProductListHistory = {
    get isRestoring() {
      return [...controllers].some((controller) => controller.restoring);
    },
    capture() {
      for (const controller of controllers) {
        controller.syncHistoryEntry();
        controller.captureContent();
        controller.capturePosition();
      }
    },
    mergeHistoryState,
    shouldRefreshForPopstate(url) {
      if (popstateRestored || skipNextPopstate) return false;
      return [...controllers].some((controller) => controller.renderedUrl !== canonicalUrl(url));
    },
  };
})();
